import React, { forwardRef, useRef, useImperativeHandle } from "react";
import { useGlobalValue } from "colbi_web_ui/lib/state/GlobalProvider";
import {
    SovosBox,
    SovosButton,
    SovosCheckboxField,
    SovosDivider,
    SovosFilterDateRange,
    SovosTextField,
    SovosTypography,
} from "mosaic-react";
import { AddCircle, Close } from "mosaic-react-icons";
import styles from "./SeriesForm.module.sass";
import { useTheme } from "mosaic-react/styles";
import { isNumeric } from "../../../utils/utils";

const SeriesForm = (props, ref) => {
    const theme = useTheme();
    const { data, status, onChange, onSave } = props;
    const { i18n, params } = useGlobalValue();

    const { projectId } = params;

    const formRef = useRef();
    const { userProjectSeries, include } = data || {};

    useImperativeHandle(ref, () => ({
        submit: () => {
            formRef.current.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
        },
    }));

    const handleChange = (e, prop, value) => {
        onChange &&
            onChange({
                originalEvent: e,
                target: {
                    value: {
                        data: {
                            ...data,
                            [prop]: value,
                        },
                    },
                },
            });
    };

    const tempObjectToCreateSeries = {
        Serie: "",
        FiscalYear: "",
        StartDate: null,
        EndDate: null,
    };

    const organizationsItems = (userProjectSeries || []).map((projectSerie, index) => {
        const { Serie, FiscalYear, StartDate, EndDate } = projectSerie || {};
        return (
            <SovosBox key={index} className={styles["container"]}>
                <SovosBox className={`${styles["group-content"]} ${styles["adjust-no-header"]}`}>
                    <SovosBox className={styles["flex"]}>
                        <SovosBox sx={{ paddingLeft: "5px" }}>
                            <SovosTextField
                                value={Serie || ""}
                                label={i18n`Serie`}
                                autoFocus={true}
                                placeholder={i18n`Enter a Serie`}
                                required
                                error={!Serie && true}
                                onChange={(e) => {
                                    const userSerie = userProjectSeries[index];
                                    const nextUserSerie = [...(userProjectSeries || [])];
                                    nextUserSerie.splice(index, 1, {
                                        ...userSerie,
                                        Serie: e.target.value || "",
                                    });
                                    handleChange(e, `userProjectSeries`, nextUserSerie);
                                }}
                            />
                        </SovosBox>
                        <SovosBox sx={{ paddingLeft: "16px" }}>
                            <SovosTextField
                                value={FiscalYear || ""}
                                label={i18n`Fiscal Year`}
                                placeholder={i18n`Enter a Fiscal Year`}
                                inputProps={{ maxLength: 4 }}
                                onChange={(e) => {
                                    const userSerie = userProjectSeries[index];
                                    const nextUserSerie = [...(userProjectSeries || [])];
                                    nextUserSerie.splice(index, 1, {
                                        ...userSerie,
                                        FiscalYear: e.target.value || "",
                                    });
                                    // TODO: Fix for inputProps type="number" not working (Already spoke to Andy)
                                    isNumeric(e.target.value) &&
                                        handleChange(e, `userProjectSeries`, nextUserSerie);
                                }}
                            />
                        </SovosBox>
                    </SovosBox>
                </SovosBox>
                <SovosBox className={styles["group-content"]}>
                    <SovosBox className={styles["flex"]}>
                        <SovosFilterDateRange
                            sx={{
                                "& .MuiInputBase-root": {
                                    height: "56px",
                                },
                            }}
                            label={""}
                            data={[
                                {
                                    from:
                                        (StartDate &&
                                            new Date(StartDate).toISOString().split("T")[0]) ||
                                        null,
                                    to:
                                        (EndDate &&
                                            new Date(EndDate).toISOString().split("T")[0]) ||
                                        null,
                                },
                            ]}
                            format={"yyyy-MM-dd"}
                            fromLabel={i18n`Start Date`}
                            onChange={(e) => {
                                const [dates] = e;
                                const userSerie = userProjectSeries[index];
                                const nextUserSerie = [...(userProjectSeries || [])];
                                nextUserSerie.splice(index, 1, {
                                    ...userSerie,
                                    StartDate: dates.from || null,
                                    EndDate: dates.to || null,
                                });
                                handleChange(e, `userProjectSeries`, nextUserSerie);
                            }}
                            toLabel={i18n`End Date`}
                            invalidStartDateMessage={i18n`Invalid Start Date value`}
                            invalidEndDateMessage={i18n`Invalid End Date value`}
                        />
                    </SovosBox>
                </SovosBox>
                <SovosBox className={styles["remove-button-container"]}>
                    <SovosButton
                        onClick={(e) => {
                            const nextOrganization = [...(userProjectSeries || [])];
                            nextOrganization.splice(index, 1);
                            handleChange(e, "userProjectSeries", nextOrganization);
                        }}
                        variant={"text"}
                        color={theme.palette.text.primary}
                        size={"small"}
                    >
                        <Close />
                    </SovosButton>
                </SovosBox>
            </SovosBox>
        );
    });
    return status !== "loading" ? (
        <form
            ref={formRef}
            onSubmit={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (formRef.current.checkValidity()) {
                    data["__typename"] = "ProjectSeries";
                    data["projectId"] = projectId;
                    data["include"] = include;
                    onSave && onSave();
                } else {
                    formRef.current.reportValidity();
                }
            }}
        >
            <SovosBox className={styles["input-field"]}>
                <SovosCheckboxField
                    checked={include || false}
                    label={i18n`Include Series?`}
                    onChange={(e) => {
                        handleChange(e, "include", e.target.checked);
                    }}
                />
                <SovosDivider
                    sx={{
                        display: "flex",
                        padding: "16px 32px",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        gap: "10px",
                    }}
                />
                <SovosBox className={styles["series-title-and-button"]}>
                    <SovosBox>
                        <SovosTypography variant={"h5"} sx={{ wordSpacing: "0.5px" }}>
                            {i18n`Series (${userProjectSeries?.length.toString()})`}
                        </SovosTypography>
                    </SovosBox>
                    <SovosBox>
                        <SovosButton
                            sx={{ position: "sticky", top: "0" }}
                            startIcon={<AddCircle />}
                            children={i18n`Add`}
                            variant={"text"}
                            onClick={(e) => {
                                handleChange(e, "userProjectSeries", [
                                    ...(userProjectSeries || []),
                                    tempObjectToCreateSeries,
                                ]);
                            }}
                        />
                    </SovosBox>
                </SovosBox>

                <SovosBox>
                    {organizationsItems?.length ? (
                        organizationsItems
                    ) : (
                        <SovosTypography variant={"small"} children={i18n`No series assigned`} />
                    )}
                </SovosBox>
            </SovosBox>
        </form>
    ) : null;
};

const SeriesFormRef = forwardRef(SeriesForm);

SeriesFormRef.queries = (args) => {
    return [
        {
            resource: "project_series",
            args: {
                projectId: args.projectId,
            },
            body: "userProjectSeries{ID, ProjectID, FiscalYear, Serie, StartDate, EndDate}, include",
        },
    ].filter((q) => q);
};

SeriesFormRef.dataMapper = ["data"];

export default SeriesFormRef;
